<template>
  <div>
    <v-btn v-if="!dialogOnly && !contentOnly" @click="openDialog" small color="primary" text>
      <v-icon small left>mdi-information-outline</v-icon>
      View details
    </v-btn>

    <v-dialog v-if="!contentOnly" v-model="dialog" max-width="700" persistent transition="dialog-transition">
    <v-card>
      <v-card-title class="primary white--text">
        <span class="text-h6">{{ providerName }}</span>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pt-4">
        <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>
        <div v-else-if="error" class="text-center py-4">
          <v-icon color="error" large>mdi-alert-circle</v-icon>
          <p class="mt-2">{{ error }}</p>
        </div>
        <div v-else-if="provider" class="provider-details">
          <div v-if="memberInfoRows.length" class="mb-4">
            <div class="text-subtitle-2 mb-2">Member information</div>
            <v-list dense class="py-0">
              <v-list-item v-for="(row, i) in memberInfoRows" :key="'mid-' + i">
                <v-list-item-content>
                  <v-list-item-subtitle>{{ row.label }}</v-list-item-subtitle>
                  <v-list-item-title>{{ row.value }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>
          <v-divider v-if="memberInfoRows.length" class="mb-4"></v-divider>
          <!-- 1. Provider Information -->
          <div class="text-subtitle-2 mb-2">Provider Information</div>
          <v-list dense class="py-0">
            <v-list-item v-if="providerName">
              <v-list-item-content>
                <v-list-item-subtitle>Name</v-list-item-subtitle>
                <v-list-item-title>{{ providerName }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="providerNpi">
              <v-list-item-content>
                <v-list-item-subtitle>NPI</v-list-item-subtitle>
                <v-list-item-title>{{ providerNpi }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="providerStatus">
              <v-list-item-content>
                <v-list-item-subtitle>Status</v-list-item-subtitle>
                <v-list-item-title>{{ providerStatus }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="providerPcp != null">
              <v-list-item-content>
                <v-list-item-subtitle>PCP</v-list-item-subtitle>
                <v-list-item-title>{{ providerPcp ? 'Yes' : 'No' }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="providerDegree">
              <v-list-item-content>
                <v-list-item-subtitle>Degree</v-list-item-subtitle>
                <v-list-item-title>{{ providerDegree }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="providerCapitated != null">
              <v-list-item-content>
                <v-list-item-subtitle>Capitated</v-list-item-subtitle>
                <v-list-item-title>{{ providerCapitated ? 'Yes' : 'No' }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-subtitle>Gender</v-list-item-subtitle>
                <v-list-item-title>{{ providerGenderLine }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item>
              <v-list-item-content>
                <v-list-item-subtitle>Board Certified</v-list-item-subtitle>
                <v-list-item-title>{{ providerBoardCertifiedLine }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="providerAcceptingNewPatients">
              <v-list-item-content>
                <v-list-item-subtitle>Accepting New Patients</v-list-item-subtitle>
                <v-list-item-title>Yes</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-list-item v-if="providerLanguages">
              <v-list-item-content>
                <v-list-item-subtitle>Languages</v-list-item-subtitle>
                <v-list-item-title>{{ providerLanguages }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <!-- 2. Specialty -->
          <div v-if="primarySpecialty || secondarySpecialty" class="mt-4">
            <v-divider class="mb-2"></v-divider>
            <div class="text-subtitle-2 mb-2">Specialty</div>
            <v-list dense class="py-0">
              <v-list-item v-if="primarySpecialty">
                <v-list-item-content>
                  <v-list-item-subtitle>Primary</v-list-item-subtitle>
                  <v-list-item-title>{{ primarySpecialty }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-if="secondarySpecialty">
                <v-list-item-content>
                  <v-list-item-subtitle>Secondary</v-list-item-subtitle>
                  <v-list-item-title>{{ secondarySpecialty }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>

          <!-- 3. Organization -->
          <div v-if="organizationName || serviceLocation || billingProvider || groupPractice" class="mt-4">
            <v-divider class="mb-2"></v-divider>
            <div class="text-subtitle-2 mb-2">Organization</div>
            <v-list dense class="py-0">
              <v-list-item v-if="organizationName">
                <v-list-item-content>
                  <v-list-item-subtitle>Organization</v-list-item-subtitle>
                  <v-list-item-title>{{ organizationName }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-if="serviceLocation">
                <v-list-item-content>
                  <v-list-item-subtitle>Service Location</v-list-item-subtitle>
                  <v-list-item-title>{{ serviceLocation }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-if="billingProvider">
                <v-list-item-content>
                  <v-list-item-subtitle>Billing Provider</v-list-item-subtitle>
                  <v-list-item-title>{{ billingProvider }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item v-if="groupPractice">
                <v-list-item-content>
                  <v-list-item-subtitle>Group Practice</v-list-item-subtitle>
                  <v-list-item-title>{{ groupPractice }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>

          <!-- 4. Address -->
          <div v-if="fullAddress || addressCounty || addressCoordinates" class="mt-4">
            <v-divider class="mb-2"></v-divider>
            <div class="text-subtitle-2 mb-2">Address</div>
            <div v-if="fullAddress" class="mb-2">{{ fullAddress }}</div>
            <div v-if="addressCounty" class="mb-2">County: {{ addressCounty }}</div>
            <div v-if="addressCoordinates" class="text-caption grey--text">{{ addressCoordinates }}</div>
          </div>

          <!-- 5. Contact -->
          <div v-if="providerPhone || providerFax" class="mt-4">
            <v-divider class="mb-2"></v-divider>
            <div class="text-subtitle-2 mb-2">Contact</div>
            <v-row dense>
              <v-col v-if="providerPhone" cols="12" sm="6">
                <div class="text-caption grey--text">Phone</div>
                <a :href="'tel:' + providerPhone">{{ providerPhone }}</a>
              </v-col>
              <v-col v-if="providerFax" cols="12" sm="6">
                <div class="text-caption grey--text">Fax</div>
                <span>{{ providerFax }}</span>
              </v-col>
            </v-row>
          </div>

          <!-- 6. Price Table -->
          <div v-if="showPriceTable && !hideRates" class="mt-4">
            <v-divider class="mb-2"></v-divider>
            <div class="text-subtitle-2 mb-2">Pricing Table</div>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Plan / Scenario</th>
                  <th>Description</th>
                  <th class="text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="showPHC">
                  <td class="font-weight-medium">PHC Plan</td>
                  <td>—</td>
                  <td class="text-right"></td>
                </tr>
                <tr v-if="showPHC && phcPcpCopay">
                  <td class="pl-4">PCP Copay</td>
                  <td>—</td>
                  <td class="text-right">{{ formatPrice(phcPcpCopay) }}</td>
                </tr>
                <tr v-if="showPHC && phcSpecialistCopay">
                  <td class="pl-4">Specialist Copay</td>
                  <td>—</td>
                  <td class="text-right">{{ formatPrice(phcSpecialistCopay) }}</td>
                </tr>
                <tr v-if="showPHC && phcNonPcpFirstVisit">
                  <td class="pl-4">Non-PCP First Visit</td>
                  <td>—</td>
                  <td class="text-right">{{ formatAmount(phcNonPcpFirstVisit, phcNonPcpFirstVisitType) }}</td>
                </tr>
                <tr v-if="showPHC && phcNonPcpFollowUp">
                  <td class="pl-4">Non-PCP Follow-up</td>
                  <td>—</td>
                  <td class="text-right">{{ formatAmount(phcNonPcpFollowUp, phcNonPcpFollowUpType) }}</td>
                </tr>
                <tr v-if="showPHC && phcMedicareAllowableRate">
                  <td class="pl-4">PHC Medicare Allowable Rate</td>
                  <td>—</td>
                  <td class="text-right">{{ formatAmount(phcMedicareAllowableRate, phcMedicareAllowableRateType, true) }}</td>
                </tr>
                <tr v-if="showPHC && capitationSubscriber && !hideCapitationPricingAndOtherInfo">
                  <td class="pl-4">Capitation (Subscriber)</td>
                  <td>—</td>
                  <td class="text-right">{{ formatPrice(capitationSubscriber) }}</td>
                </tr>
                <tr v-if="showPHC && capitationDependents && !hideCapitationPricingAndOtherInfo">
                  <td class="pl-4">Capitation (Dependents 2–5)</td>
                  <td>—</td>
                  <td class="text-right">{{ formatPrice(capitationDependents) }}</td>
                </tr>
                <tr v-if="showPHC && phcMedicalOrOtherServices">
                  <td class="pl-4">Medical or other services</td>
                  <td>—</td>
                  <td class="text-right">{{ formatAmount(phcMedicalOrOtherServices, phcMedicalOrOtherServicesType, true) }}</td>
                </tr>
                <tr v-if="showPHC && phcDiagnosticsServices">
                  <td class="pl-4">Diagnostics Services</td>
                  <td>—</td>
                  <td class="text-right">{{ formatAmount(phcDiagnosticsServices, phcDiagnosticsServicesType, true) }}</td>
                </tr>
                <tr v-if="showDPO">
                  <td class="font-weight-medium pt-2">DPO Plan</td>
                  <td>—</td>
                  <td class="text-right"></td>
                </tr>
                <tr v-if="showDPO && dpoFirstVisit">
                  <td class="pl-4">First Visit</td>
                  <td>—</td>
                  <td class="text-right">{{ formatAmount(dpoFirstVisit, dpoFirstVisitType) }}</td>
                </tr>
                <tr v-if="showDPO && dpoFollowUp">
                  <td class="pl-4">Follow-up</td>
                  <td>—</td>
                  <td class="text-right">{{ formatAmount(dpoFollowUp, dpoFollowUpType) }}</td>
                </tr>
                <tr v-if="showDPO && dpoMedicareAllowableRate">
                  <td class="pl-4">DPO Medicare Allowable Rate</td>
                  <td>—</td>
                  <td class="text-right">{{ formatAmount(dpoMedicareAllowableRate, dpoMedicareAllowableRateType, true) }}</td>
                </tr>
                <tr v-if="showDPO && dpoMedicalOrOtherServices">
                  <td class="pl-4">Medical or other services</td>
                  <td>—</td>
                  <td class="text-right">{{ formatAmount(dpoMedicalOrOtherServices, dpoMedicalOrOtherServicesType, true) }}</td>
                </tr>
                <tr v-if="showDPO && dpoDiagnosticsServices">
                  <td class="pl-4">Diagnostics Services</td>
                  <td>—</td>
                  <td class="text-right">{{ formatAmount(dpoDiagnosticsServices, dpoDiagnosticsServicesType, true) }}</td>
                </tr>
                <tr v-if="showLegacy">
                  <td class="font-weight-medium pt-2">Legacy</td>
                  <td>—</td>
                  <td class="text-right"></td>
                </tr>
                <tr v-if="showLegacy && legacyCopay">
                  <td class="pl-4">Copay</td>
                  <td>—</td>
                  <td class="text-right">{{ formatPrice(legacyCopay) }}</td>
                </tr>
                <tr v-if="showLegacy && legacyCopayPrimaryCareCapitated && !hideCapitationPricingAndOtherInfo">
                  <td class="pl-4">Copay Primary Care (Capitated)</td>
                  <td>—</td>
                  <td class="text-right">{{ formatPrice(legacyCopayPrimaryCareCapitated) }}</td>
                </tr>
                <tr v-if="showLegacy && medicareFeeSchedule">
                  <td class="pl-4">Medicare Fee Schedule</td>
                  <td>—</td>
                  <td class="text-right">{{ formatAmount(medicareFeeSchedule, medicareFeeScheduleType, true) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>

          <!-- 7. PHC Services -->
          <div v-if="phcServices.length > 0 && !hideRates" class="mt-4">
            <v-divider class="mb-2"></v-divider>
            <div class="text-subtitle-2 mb-2">PHC Services</div>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Service</th>
                  <th>CPT</th>
                  <th>Fee Schedule</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(svc, i) in phcServices" :key="i">
                  <td>{{ svc.name }}</td>
                  <td>{{ svc.cptCode }}</td>
                  <td>{{ formatAmount(svc.feeSchedule, svc.feeScheduleType, true) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>

          <!-- 8. DPO Services -->
          <div v-if="dpoServices.length > 0 && !hideRates" class="mt-4">
            <v-divider class="mb-2"></v-divider>
            <div class="text-subtitle-2 mb-2">DPO Services</div>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Service</th>
                  <th>CPT</th>
                  <th>Fee Schedule</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(svc, i) in dpoServices" :key="i">
                  <td>{{ svc.name }}</td>
                  <td>{{ svc.cptCode }}</td>
                  <td>{{ formatAmount(svc.feeSchedule, svc.feeScheduleType, true) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>

          <!-- 9. PCP Services (only when no PHC/DPO Services) -->
          <div v-if="showPcpServices && pcpServices.length > 0 && !hideRates" class="mt-4">
            <v-divider class="mb-2"></v-divider>
            <div class="text-subtitle-2 mb-2">PCP Services</div>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Service</th>
                  <th>CPT</th>
                  <th>Fee Schedule</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(svc, i) in pcpServices" :key="i">
                  <td>{{ svc.name }}</td>
                  <td>{{ svc.cptCode }}</td>
                  <td>{{ formatAmount(svc.feeSchedule, svc.feeScheduleType, true) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>

          <!-- 10. Other Information -->
          <div v-if="otherInfoFields.length > 0 && !hideCapitationPricingAndOtherInfo" class="mt-4">
            <v-divider class="mb-2"></v-divider>
            <div class="text-subtitle-2 mb-2">Other Information</div>
            <v-list dense class="py-0">
              <v-list-item v-for="(item, i) in otherInfoFields" :key="i">
                <v-list-item-content>
                  <v-list-item-subtitle>{{ item.label }}</v-list-item-subtitle>
                  <v-list-item-title>{{ item.value }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>

          <!-- 11. All Locations (when more than 1) -->
          <div v-if="locations.length > 1" class="mt-4">
            <v-divider class="mb-2"></v-divider>
            <div class="text-subtitle-2 mb-2">All Locations</div>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>City</th>
                  <th>State</th>
                  <th>Status</th>
                  <th>Specialty</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(loc, i) in locations" :key="i">
                  <td>{{ loc.ServiceLocationCity || loc.serviceLocationCity }}</td>
                  <td>{{ loc.ServiceLocationState || loc.serviceLocationState }}</td>
                  <td>{{ loc.Status || loc.status }}</td>
                  <td>{{ loc.PrimarySpecialty || loc.primarySpecialty }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
        </div>
      </v-card-text>
    </v-card>
    </v-dialog>

    <!-- Modo contentOnly: só o card, para uso dentro de v-dialog do pai -->
    <v-card v-if="contentOnly" :class="{ 'provider-details-page-mode': pageMode }">
      <v-card-title v-if="!pageMode" class="primary white--text">
        <span class="text-h6">{{ providerName }}</span>
        <v-spacer></v-spacer>
        <v-btn v-if="!hideCloseButton" icon dark @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <div v-else-if="pageMode" class="provider-page-header pa-5">
        <div class="d-flex align-center">
          <v-avatar size="44" color="primary" class="mr-3">
            <v-icon size="24" color="white">mdi-account-tie</v-icon>
          </v-avatar>
          <div>
            <h2 class="text-h5 font-weight-bold mb-0">{{ loading ? 'Loading your profile...' : (providerName || 'Provider') }}</h2>
            <p v-if="providerNpi && !loading" class="caption grey--text mb-0 mt-1">NPI: {{ providerNpi }}</p>
          </div>
        </div>
      </div>
      <v-card-text :class="pageMode ? 'pa-5' : 'pt-4'">
        <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>
        <div v-else-if="error" class="text-center py-4">
          <v-icon color="error" large>mdi-alert-circle</v-icon>
          <p class="mt-2">{{ error }}</p>
        </div>
        <div v-else-if="provider" class="provider-details">
          <div v-if="memberInfoRows.length" class="mb-4">
            <div class="text-subtitle-2 mb-2">Member information</div>
            <v-list dense class="py-0">
              <v-list-item v-for="(row, i) in memberInfoRows" :key="'mip-' + i">
                <v-list-item-content>
                  <v-list-item-subtitle>{{ row.label }}</v-list-item-subtitle>
                  <v-list-item-title>{{ row.value }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>
          <v-divider v-if="memberInfoRows.length" class="mb-4"></v-divider>
          <!-- 1. Provider Information -->
          <div class="text-subtitle-2 mb-2">Provider Information</div>
          <v-list dense class="py-0">
            <v-list-item v-if="providerName"><v-list-item-content><v-list-item-subtitle>Name</v-list-item-subtitle><v-list-item-title>{{ providerName }}</v-list-item-title></v-list-item-content></v-list-item>
            <v-list-item v-if="providerNpi"><v-list-item-content><v-list-item-subtitle>NPI</v-list-item-subtitle><v-list-item-title>{{ providerNpi }}</v-list-item-title></v-list-item-content></v-list-item>
            <v-list-item v-if="providerStatus"><v-list-item-content><v-list-item-subtitle>Status</v-list-item-subtitle><v-list-item-title>{{ providerStatus }}</v-list-item-title></v-list-item-content></v-list-item>
            <v-list-item v-if="providerPcp != null"><v-list-item-content><v-list-item-subtitle>PCP</v-list-item-subtitle><v-list-item-title>{{ providerPcp ? 'Yes' : 'No' }}</v-list-item-title></v-list-item-content></v-list-item>
            <v-list-item v-if="providerDegree"><v-list-item-content><v-list-item-subtitle>Degree</v-list-item-subtitle><v-list-item-title>{{ providerDegree }}</v-list-item-title></v-list-item-content></v-list-item>
            <v-list-item v-if="providerCapitated != null"><v-list-item-content><v-list-item-subtitle>Capitated</v-list-item-subtitle><v-list-item-title>{{ providerCapitated ? 'Yes' : 'No' }}</v-list-item-title></v-list-item-content></v-list-item>
            <v-list-item><v-list-item-content><v-list-item-subtitle>Gender</v-list-item-subtitle><v-list-item-title>{{ providerGenderLine }}</v-list-item-title></v-list-item-content></v-list-item>
            <v-list-item><v-list-item-content><v-list-item-subtitle>Board Certified</v-list-item-subtitle><v-list-item-title>{{ providerBoardCertifiedLine }}</v-list-item-title></v-list-item-content></v-list-item>
            <v-list-item v-if="providerAcceptingNewPatients"><v-list-item-content><v-list-item-subtitle>Accepting New Patients</v-list-item-subtitle><v-list-item-title>Yes</v-list-item-title></v-list-item-content></v-list-item>
            <v-list-item v-if="providerLanguages"><v-list-item-content><v-list-item-subtitle>Languages</v-list-item-subtitle><v-list-item-title>{{ providerLanguages }}</v-list-item-title></v-list-item-content></v-list-item>
          </v-list>
          <div v-if="primarySpecialty || secondarySpecialty" class="mt-4">
            <v-divider class="mb-2"></v-divider>
            <div class="text-subtitle-2 mb-2">Specialty</div>
            <v-list dense class="py-0">
              <v-list-item v-if="primarySpecialty"><v-list-item-content><v-list-item-subtitle>Primary</v-list-item-subtitle><v-list-item-title>{{ primarySpecialty }}</v-list-item-title></v-list-item-content></v-list-item>
              <v-list-item v-if="secondarySpecialty"><v-list-item-content><v-list-item-subtitle>Secondary</v-list-item-subtitle><v-list-item-title>{{ secondarySpecialty }}</v-list-item-title></v-list-item-content></v-list-item>
            </v-list>
          </div>
          <div v-if="organizationName || serviceLocation || billingProvider || groupPractice" class="mt-4">
            <v-divider class="mb-2"></v-divider>
            <div class="text-subtitle-2 mb-2">Organization</div>
            <v-list dense class="py-0">
              <v-list-item v-if="organizationName"><v-list-item-content><v-list-item-subtitle>Organization</v-list-item-subtitle><v-list-item-title>{{ organizationName }}</v-list-item-title></v-list-item-content></v-list-item>
              <v-list-item v-if="serviceLocation"><v-list-item-content><v-list-item-subtitle>Service Location</v-list-item-subtitle><v-list-item-title>{{ serviceLocation }}</v-list-item-title></v-list-item-content></v-list-item>
              <v-list-item v-if="billingProvider"><v-list-item-content><v-list-item-subtitle>Billing Provider</v-list-item-subtitle><v-list-item-title>{{ billingProvider }}</v-list-item-title></v-list-item-content></v-list-item>
              <v-list-item v-if="groupPractice"><v-list-item-content><v-list-item-subtitle>Group Practice</v-list-item-subtitle><v-list-item-title>{{ groupPractice }}</v-list-item-title></v-list-item-content></v-list-item>
            </v-list>
          </div>
          <div v-if="fullAddress || addressCounty || addressCoordinates" class="mt-4">
            <v-divider class="mb-2"></v-divider>
            <div class="text-subtitle-2 mb-2">Address</div>
            <div v-if="fullAddress" class="mb-2">{{ fullAddress }}</div>
            <div v-if="addressCounty" class="mb-2">County: {{ addressCounty }}</div>
            <div v-if="addressCoordinates" class="text-caption grey--text">{{ addressCoordinates }}</div>
          </div>
          <div v-if="providerPhone || providerFax" class="mt-4">
            <v-divider class="mb-2"></v-divider>
            <div class="text-subtitle-2 mb-2">Contact</div>
            <v-row dense>
              <v-col v-if="providerPhone" cols="12" sm="6"><div class="text-caption grey--text">Phone</div><a :href="'tel:' + providerPhone">{{ providerPhone }}</a></v-col>
              <v-col v-if="providerFax" cols="12" sm="6"><div class="text-caption grey--text">Fax</div><span>{{ providerFax }}</span></v-col>
            </v-row>
          </div>
          <div v-if="showPriceTable && !hideRates" class="mt-4">
            <v-divider class="mb-2"></v-divider>
            <div class="text-subtitle-2 mb-2">Pricing Table</div>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th>Plan / Scenario</th>
                  <th>Description</th>
                  <th class="text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="showPHC"><td class="font-weight-medium">PHC Plan</td><td>—</td><td class="text-right"></td></tr>
                <tr v-if="showPHC && phcPcpCopay"><td class="pl-4">PCP Copay</td><td>—</td><td class="text-right">{{ formatPrice(phcPcpCopay) }}</td></tr>
                <tr v-if="showPHC && phcSpecialistCopay"><td class="pl-4">Specialist Copay</td><td>—</td><td class="text-right">{{ formatPrice(phcSpecialistCopay) }}</td></tr>
                <tr v-if="showPHC && phcNonPcpFirstVisit"><td class="pl-4">Non-PCP First Visit</td><td>—</td><td class="text-right">{{ formatAmount(phcNonPcpFirstVisit, phcNonPcpFirstVisitType) }}</td></tr>
                <tr v-if="showPHC && phcNonPcpFollowUp"><td class="pl-4">Non-PCP Follow-up</td><td>—</td><td class="text-right">{{ formatAmount(phcNonPcpFollowUp, phcNonPcpFollowUpType) }}</td></tr>
                <tr v-if="showPHC && phcMedicareAllowableRate"><td class="pl-4">PHC Medicare Allowable Rate</td><td>—</td><td class="text-right">{{ formatAmount(phcMedicareAllowableRate, phcMedicareAllowableRateType, true) }}</td></tr>
                <tr v-if="showPHC && capitationSubscriber && !hideCapitationPricingAndOtherInfo"><td class="pl-4">Capitation (Subscriber)</td><td>—</td><td class="text-right">{{ formatPrice(capitationSubscriber) }}</td></tr>
                <tr v-if="showPHC && capitationDependents && !hideCapitationPricingAndOtherInfo"><td class="pl-4">Capitation (Dependents 2–5)</td><td>—</td><td class="text-right">{{ formatPrice(capitationDependents) }}</td></tr>
                <tr v-if="showPHC && phcMedicalOrOtherServices"><td class="pl-4">Medical or other services</td><td>—</td><td class="text-right">{{ formatAmount(phcMedicalOrOtherServices, phcMedicalOrOtherServicesType, true) }}</td></tr>
                <tr v-if="showPHC && phcDiagnosticsServices"><td class="pl-4">Diagnostics Services</td><td>—</td><td class="text-right">{{ formatAmount(phcDiagnosticsServices, phcDiagnosticsServicesType, true) }}</td></tr>
                <tr v-if="showDPO"><td class="font-weight-medium pt-2">DPO Plan</td><td>—</td><td class="text-right"></td></tr>
                <tr v-if="showDPO && dpoFirstVisit"><td class="pl-4">First Visit</td><td>—</td><td class="text-right">{{ formatAmount(dpoFirstVisit, dpoFirstVisitType) }}</td></tr>
                <tr v-if="showDPO && dpoFollowUp"><td class="pl-4">Follow-up</td><td>—</td><td class="text-right">{{ formatAmount(dpoFollowUp, dpoFollowUpType) }}</td></tr>
                <tr v-if="showDPO && dpoMedicareAllowableRate"><td class="pl-4">DPO Medicare Allowable Rate</td><td>—</td><td class="text-right">{{ formatAmount(dpoMedicareAllowableRate, dpoMedicareAllowableRateType, true) }}</td></tr>
                <tr v-if="showDPO && dpoMedicalOrOtherServices"><td class="pl-4">Medical or other services</td><td>—</td><td class="text-right">{{ formatAmount(dpoMedicalOrOtherServices, dpoMedicalOrOtherServicesType, true) }}</td></tr>
                <tr v-if="showDPO && dpoDiagnosticsServices"><td class="pl-4">Diagnostics Services</td><td>—</td><td class="text-right">{{ formatAmount(dpoDiagnosticsServices, dpoDiagnosticsServicesType, true) }}</td></tr>
                <tr v-if="showLegacy"><td class="font-weight-medium pt-2">Legacy</td><td>—</td><td class="text-right"></td></tr>
                <tr v-if="showLegacy && legacyCopay"><td class="pl-4">Copay</td><td>—</td><td class="text-right">{{ formatPrice(legacyCopay) }}</td></tr>
                <tr v-if="showLegacy && legacyCopayPrimaryCareCapitated && !hideCapitationPricingAndOtherInfo"><td class="pl-4">Copay Primary Care (Capitated)</td><td>—</td><td class="text-right">{{ formatPrice(legacyCopayPrimaryCareCapitated) }}</td></tr>
                <tr v-if="showLegacy && medicareFeeSchedule"><td class="pl-4">Medicare Fee Schedule</td><td>—</td><td class="text-right">{{ formatAmount(medicareFeeSchedule, medicareFeeScheduleType, true) }}</td></tr>
              </tbody>
            </v-simple-table>
          </div>
          <div v-if="phcServices.length > 0 && !hideRates" class="mt-4">
            <v-divider class="mb-2"></v-divider>
            <div class="text-subtitle-2 mb-2">PHC Services</div>
            <v-simple-table dense>
              <thead><tr><th>Service</th><th>CPT</th><th>Fee Schedule</th></tr></thead>
              <tbody>
                <tr v-for="(svc, i) in phcServices" :key="i">
                  <td>{{ svc.name }}</td>
                  <td>{{ svc.cptCode }}</td>
                  <td>{{ formatAmount(svc.feeSchedule, svc.feeScheduleType, true) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
          <div v-if="dpoServices.length > 0 && !hideRates" class="mt-4">
            <v-divider class="mb-2"></v-divider>
            <div class="text-subtitle-2 mb-2">DPO Services</div>
            <v-simple-table dense>
              <thead><tr><th>Service</th><th>CPT</th><th>Fee Schedule</th></tr></thead>
              <tbody>
                <tr v-for="(svc, i) in dpoServices" :key="i">
                  <td>{{ svc.name }}</td>
                  <td>{{ svc.cptCode }}</td>
                  <td>{{ formatAmount(svc.feeSchedule, svc.feeScheduleType, true) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
          <div v-if="showPcpServices && pcpServices.length > 0 && !hideRates" class="mt-4">
            <v-divider class="mb-2"></v-divider>
            <div class="text-subtitle-2 mb-2">PCP Services</div>
            <v-simple-table dense>
              <thead><tr><th>Service</th><th>CPT</th><th>Fee Schedule</th></tr></thead>
              <tbody>
                <tr v-for="(svc, i) in pcpServices" :key="i">
                  <td>{{ svc.name }}</td>
                  <td>{{ svc.cptCode }}</td>
                  <td>{{ formatAmount(svc.feeSchedule, svc.feeScheduleType, true) }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
          <div v-if="otherInfoFields.length > 0 && !hideCapitationPricingAndOtherInfo" class="mt-4">
            <v-divider class="mb-2"></v-divider>
            <div class="text-subtitle-2 mb-2">Other Information</div>
            <v-list dense class="py-0">
              <v-list-item v-for="(item, i) in otherInfoFields" :key="i">
                <v-list-item-content>
                  <v-list-item-subtitle>{{ item.label }}</v-list-item-subtitle>
                  <v-list-item-title>{{ item.value }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>
          <div v-if="locations.length > 1" class="mt-4">
            <v-divider class="mb-2"></v-divider>
            <div class="text-subtitle-2 mb-2">All Locations</div>
            <v-simple-table dense>
              <thead><tr><th>City</th><th>State</th><th>Status</th><th>Specialty</th></tr></thead>
              <tbody>
                <tr v-for="(loc, i) in locations" :key="i">
                  <td>{{ loc.ServiceLocationCity || loc.serviceLocationCity }}</td>
                  <td>{{ loc.ServiceLocationState || loc.serviceLocationState }}</td>
                  <td>{{ loc.Status || loc.status }}</td>
                  <td>{{ loc.PrimarySpecialty || loc.primarySpecialty }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import axios from 'axios';
import { boardCertifiedDisplay, buildMemberInfoRows, providerGenderLine as providerGenderLineFromRecord } from '../utils/fhcProviderMemberDisplay';

export default {
  name: 'ProviderDetailsFHC',
  props: {
    provider: {
      type: Object,
      default: () => ({})
    },
    dialogOnly: {
      type: Boolean,
      default: false
    },
    contentOnly: {
      type: Boolean,
      default: false
    },
    hideRates: {
      type: Boolean,
      default: false
    },
    /** Provider Search: ocultar linhas de capitation na Pricing Table e a secção Other Information */
    hideCapitationPricingAndOtherInfo: {
      type: Boolean,
      default: false
    },
    hideCloseButton: {
      type: Boolean,
      default: false
    },
    pageMode: {
      type: Boolean,
      default: false
    },
    /** When set (e.g. member enrollment), shows Name, Member ID, DOB above provider info. */
    memberInfo: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      dialog: false,
      loading: false,
      error: null,
      providerDetails: null
    };
  },
  computed: {
    providerNpi() {
      const p = this.providerDetails || this.provider;
      return (p && p.provider_id) || (p && p.PROVIDER_ID) || (p && p.NPI) || (p && p.RenderingNPI) || '';
    },
    providerName() {
      const p = this.providerDetails || this.provider;
      if (!p) return 'Provider';
      return p.RenderingProviderFullName || p.PROVIDER_NAME || ((p.RENDERING_PROVIDER_FIRST_NAME || '') + ' ' + (p.RENDERING_PROVIDER_LAST_NAME || '')).trim() || 'Provider';
    },
    providerStatus() {
      const p = this.providerDetails || this.provider;
      return (p && p.Status) || (p && p.STATUS) || '';
    },
    providerPcp() {
      const p = this.providerDetails || this.provider;
      if (!p) return null;
      const v = p.PCP != null ? p.PCP : (p.pcp != null ? p.pcp : (p.isPCP != null ? p.isPCP : p.isPcp));
      return v === true || v === 'Yes' || v === 'yes';
    },
    providerDegree() {
      const p = this.providerDetails || this.provider;
      return (p && p.DEGREE) || (p && p.Degree) || '';
    },
    providerCapitated() {
      const p = this.providerDetails || this.provider;
      if (!p) return null;
      const v = p.CAPITATED != null ? p.CAPITATED : (p.Capitated != null ? p.Capitated : p.capitated);
      return v === true || v === 'Yes' || v === 'yes';
    },
    memberInfoRows() {
      return buildMemberInfoRows(this.memberInfo);
    },
    providerGenderLine() {
      const p = this.providerDetails || this.provider;
      return providerGenderLineFromRecord(p);
    },
    providerBoardCertifiedLine() {
      const p = this.providerDetails || this.provider;
      return boardCertifiedDisplay(p) || '—';
    },
    providerAcceptingNewPatients() {
      const d = this.providerDetails;
      const p = this.provider;
      const from = (obj) => {
        if (!obj) return undefined;
        const keys = ['ACCEPTING_NEW_PATIENTS', 'AcceptingNewPatients', 'acceptingNewPatients', 'accepting_new_patients', 'ACCEPTING_NEW', 'IsAcceptingNewPatients'];
        for (let i = 0; i < keys.length; i++) {
          const v = obj[keys[i]];
          if (v !== null && v !== undefined) return v;
        }
        return undefined;
      };
      const fromD = from(d);
      const v = fromD !== undefined ? fromD : from(p);
      return this._isTruthy(v);
    },
    providerLanguages() {
      const d = this.providerDetails;
      const p = this.provider;
      const sources = [d, p].filter(Boolean);
      for (const src of sources) {
        const langs = src.LANGUAGES || src.Languages || src.languages;
        if (Array.isArray(langs) && langs.length > 0) {
          const s = langs.map((l) => (typeof l === 'object' && l != null && (l.name || l.Name)) ? (l.name || l.Name) : String(l)).filter(Boolean).join(', ');
          if (s) return s;
        }
        const lang = src.LANGUAGE || src.Language || src.language;
        if (lang != null && String(lang).trim()) return String(lang).trim();
      }
      return '';
    },
    primarySpecialty() {
      const p = this.providerDetails || this.provider;
      return (p && p.PRIMARY_SPECIALTY) || (p && p.PrimarySpecialty) || (p && p.primarySpecialty) || '';
    },
    secondarySpecialty() {
      const p = this.providerDetails || this.provider;
      return (p && p.SECONDARY_SPECIALTY) || (p && p.SecondarySpecialty) || (p && p.secondarySpecialty) || '';
    },
    organizationName() {
      const p = this.providerDetails || this.provider;
      return (p && p.ORGANIZATION_NAME) || (p && p.OrganizationName) || (p && p.ORG_NAME) || '';
    },
    serviceLocation() {
      const p = this.providerDetails || this.provider;
      return (p && p.SERVICE_LOCATION_ADDRESS_1) || (p && p.ServiceLocationAddress1) || '';
    },
    billingProvider() {
      const p = this.providerDetails || this.provider;
      return (p && p.BILLING_PROVIDER_NAME) || (p && p.BillingProviderName) || '';
    },
    groupPractice() {
      const p = this.providerDetails || this.provider;
      return (p && p.GROUP_PRACTICE) || (p && p.GroupPractice) || '';
    },
    addressCounty() {
      const p = this.providerDetails || this.provider;
      return (p && p.COUNTY) || (p && p.County) || '';
    },
    addressCoordinates() {
      const p = this.providerDetails || this.provider;
      const lat = (p && p.SERVICE_LOCATION_LAT) != null ? (p && p.SERVICE_LOCATION_LAT) : (p && p.ServiceLocationLat);
      const lon = (p && p.SERVICE_LOCATION_LON) != null ? (p && p.SERVICE_LOCATION_LON) : (p && p.ServiceLocationLon);
      if (lat != null && lon != null) return `Lat: ${lat}, Lon: ${lon}`;
      return '';
    },
    fullAddress() {
      const p = this.providerDetails || this.provider;
      if (!p) return '';
      const addr = p.ServiceLocationAddress1 || p.SERVICE_LOCATION_ADDRESS_1 || '';
      const addr2 = p.ServiceLocationAddress2 || p.SERVICE_LOCATION_ADDRESS_2 || '';
      const city = p.ServiceLocationCity || p.SERVICE_LOCATION_CITY || '';
      const state = p.ServiceLocationState || p.SERVICE_LOCATION_STATE || '';
      const zip = p.ServiceLocationZip || p.SERVICE_LOCATION_ZIP || '';
      const parts = [addr, addr2, [city, state].filter(Boolean).join(', '), zip].filter(Boolean);
      return parts.length ? parts.join(', ') : (p.Address || p.ADDRESS || '');
    },
    providerPhone() {
      const p = this.providerDetails || this.provider;
      return (p && p.ServiceLocationPhone) || (p && p.SERVICE_LOCATION_PHONE) || (p && p.Phone) || (p && p.BILLING_PHONE) || '';
    },
    providerFax() {
      const p = this.providerDetails || this.provider;
      return (p && p.ServiceLocationFax) || (p && p.SERVICE_LOCATION_FAX) || (p && p.Fax) || '';
    },
    locations() {
      const p = this.providerDetails || this.provider;
      const locs = (p && p.Locations) || (p && p.locations) || [];
      return Array.isArray(locs) ? locs : [];
    },
    showPHC() {
      const p = this.providerDetails || this.provider;
      const v = (p && p.PHC) != null ? p.PHC : (p && p.phc);
      return v === true || v === 'Yes' || v === 'yes';
    },
    showDPO() {
      const p = this.providerDetails || this.provider;
      const v = (p && p.DPO) != null ? p.DPO : (p && p.dpo);
      return v === true || v === 'Yes' || v === 'yes';
    },
    showLegacy() {
      return !this.showPHC && !this.showDPO;
    },
    phcPcpCopay() { return this._get('PHC_PCP_COPAY', 'phcPcpCopay'); },
    phcSpecialistCopay() { return this._get('PHC_SPECIALIST_COPAY', 'phcSpecialistCopay'); },
    phcNonPcpFirstVisit() { return this._get('PHC_NON_PCP_FIRST_VISIT', 'phcNonPcpFirstVisit'); },
    phcNonPcpFirstVisitType() { return this._get('PHC_NON_PCP_FIRST_VISIT_TYPE', 'phcNonPcpFirstVisitType'); },
    phcNonPcpFollowUp() { return this._get('PHC_NON_PCP_FOLLOW_UP', 'phcNonPcpFollowUp'); },
    phcNonPcpFollowUpType() { return this._get('PHC_NON_PCP_FOLLOW_UP_TYPE', 'phcNonPcpFollowUpType'); },
    capitationSubscriber() { return this._get('CAPITATION_PAY_SUBSCRIBER_OR_FIRST', 'capitationPaySubscriberOrFirst'); },
    capitationDependents() { return this._get('CAPITATION_PAY_DEPENDENTS_2_TO_5', 'capitationPayDependents2To5'); },
    dpoFirstVisit() { return this._get('DPO_FIRST_VISIT', 'dpoFirstVisit'); },
    dpoFirstVisitType() { return this._get('DPO_FIRST_VISIT_TYPE', 'dpoFirstVisitType'); },
    dpoFollowUp() { return this._get('DPO_FOLLOW_UP', 'dpoFollowUp'); },
    dpoFollowUpType() { return this._get('DPO_FOLLOW_UP_TYPE', 'dpoFollowUpType'); },
    legacyCopay() { return this._get('Copay', 'COPAY', 'copay'); },
    medicareFeeSchedule() { return this._get('MedicareFeeSchedule', 'MEDICARE_FEE_SCHEDULE', 'medicareFeeSchedule'); },
    medicareFeeScheduleType() { return this._get('MEDICARE_FEE_SCHEDULE_TYPE', 'medicareFeeScheduleType'); },
    phcMedicareAllowableRate() {
      const v = this._get('PHC_MEDICARE_ALLOWABLE_RATE', 'phcMedicareAllowableRate');
      if (v) return v;
      const svcs = this.pcpServices;
      return svcs.length ? (svcs[0].phcMedicare || '') : '';
    },
    phcMedicareAllowableRateType() {
      const v = this._get('PHC_MEDICARE_ALLOWABLE_RATE_TYPE', 'phcMedicareAllowableRateType');
      if (v) return v;
      const svcs = this.pcpServices;
      return svcs.length ? (svcs[0].phcMedicareType || '') : '';
    },
    dpoMedicareAllowableRate() {
      const v = this._get('DPO_MEDICARE_ALLOWABLE_RATE', 'dpoMedicareAllowableRate');
      if (v) return v;
      const svcs = this.pcpServices;
      return svcs.length ? (svcs[0].dpoMedicare || '') : '';
    },
    dpoMedicareAllowableRateType() {
      const v = this._get('DPO_MEDICARE_ALLOWABLE_RATE_TYPE', 'dpoMedicareAllowableRateType');
      if (v) return v;
      const svcs = this.pcpServices;
      return svcs.length ? (svcs[0].dpoMedicareType || '') : '';
    },
    phcMedicalOrOtherServices() { return this._get('PHC_MEDICAL_OR_OTHER_SERVICES', 'phcMedicalOrOtherServices'); },
    phcMedicalOrOtherServicesType() { return this._get('PHC_MEDICAL_OR_OTHER_SERVICES_TYPE', 'phcMedicalOrOtherServicesType'); },
    phcDiagnosticsServices() { return this._get('PHC_DIAGNOSTICS_SERVICES', 'phcDiagnosticsServices'); },
    phcDiagnosticsServicesType() { return this._get('PHC_DIAGNOSTICS_SERVICES_TYPE', 'phcDiagnosticsServicesType'); },
    dpoMedicalOrOtherServices() { return this._get('DPO_MEDICAL_OR_OTHER_SERVICES', 'dpoMedicalOrOtherServices'); },
    dpoMedicalOrOtherServicesType() { return this._get('DPO_MEDICAL_OR_OTHER_SERVICES_TYPE', 'dpoMedicalOrOtherServicesType'); },
    dpoDiagnosticsServices() { return this._get('DPO_DIAGNOSTICS_SERVICES', 'dpoDiagnosticsServices'); },
    dpoDiagnosticsServicesType() { return this._get('DPO_DIAGNOSTICS_SERVICES_TYPE', 'dpoDiagnosticsServicesType'); },
    legacyCopayPrimaryCareCapitated() { return this._get('COPAY_PRIMARY_CARE_CAPITATED', 'copayPrimaryCareCapitated', 'CopayPrimaryCareCapitated'); },
    showPriceTable() {
      return this.showPHC || this.showDPO || this.showLegacy;
    },
    phcServices() {
      const p = this.providerDetails || this.provider;
      const arr = (p && p.PHC_SERVICES) || (p && p.phcServices);
      return this._normalizeServicesArray(arr);
    },
    dpoServices() {
      const p = this.providerDetails || this.provider;
      const arr = (p && p.DPO_SERVICES) || (p && p.dpoServices);
      return this._normalizeServicesArray(arr);
    },
    showPcpServices() {
      return this.phcServices.length === 0 && this.dpoServices.length === 0;
    },
    pcpServices() {
      const p = this.providerDetails || this.provider;
      const arr = (p && p.PCP_SERVICES) || (p && p.pcpServices) || (p && p.SERVICES) || (p && p.services);
      if (!Array.isArray(arr)) return [];
      const phcAtProvider = this._get('PHC_MEDICARE_ALLOWABLE_RATE', 'phcMedicareAllowableRate');
      const phcTypeAtProvider = this._get('PHC_MEDICARE_ALLOWABLE_RATE_TYPE', 'phcMedicareAllowableRateType');
      const dpoAtProvider = this._get('DPO_MEDICARE_ALLOWABLE_RATE', 'dpoMedicareAllowableRate');
      const dpoTypeAtProvider = this._get('DPO_MEDICARE_ALLOWABLE_RATE_TYPE', 'dpoMedicareAllowableRateType');
      return arr
        .map((svc) => {
          if (!svc || typeof svc !== 'object') return null;
          const name = svc.name != null ? svc.name : (svc.Name != null ? svc.Name : (svc.NAME != null ? svc.NAME : ''));
          const cptCode = svc.cptCode != null ? svc.cptCode : (svc.CPT_CODE != null ? svc.CPT_CODE : (svc.CPTCode != null ? svc.CPTCode : ''));
          const feeSchedule = svc.feeSchedule != null ? svc.feeSchedule : (svc.FeeSchedule != null ? svc.FeeSchedule : (svc.FEE_SCHEDULE != null ? svc.FEE_SCHEDULE : ''));
          const feeScheduleType = svc.feeScheduleType != null ? svc.feeScheduleType : (svc.FeeScheduleType != null ? svc.FeeScheduleType : '');
          const phcMedicare = this._getFromObj(svc, 'PHC_MEDICARE_ALLOWABLE_RATE', 'phcMedicareAllowableRate', 'PhcMedicareAllowableRate', 'phc_medicare_allowable_rate') || phcAtProvider;
          const phcMedicareType = this._getFromObj(svc, 'PHC_MEDICARE_ALLOWABLE_RATE_TYPE', 'phcMedicareAllowableRateType', 'PhcMedicareAllowableRateType') || phcTypeAtProvider;
          const dpoMedicare = this._getFromObj(svc, 'DPO_MEDICARE_ALLOWABLE_RATE', 'dpoMedicareAllowableRate', 'DpoMedicareAllowableRate', 'dpo_medicare_allowable_rate') || dpoAtProvider;
          const dpoMedicareType = this._getFromObj(svc, 'DPO_MEDICARE_ALLOWABLE_RATE_TYPE', 'dpoMedicareAllowableRateType', 'DpoMedicareAllowableRateType') || dpoTypeAtProvider;
          if (!name && !cptCode && !feeSchedule && !phcMedicare && !dpoMedicare) return null;
          return { name, cptCode, feeSchedule, feeScheduleType, phcMedicare, phcMedicareType, dpoMedicare, dpoMedicareType };
        })
        .filter(Boolean);
    },
    otherInfoFields() {
      const p = this.providerDetails || this.provider;
      if (!p) return [];
      const known = new Set([
        'provider_id', 'PROVIDER_ID', 'NPI', 'RenderingNPI', 'PROVIDER_NAME', 'RenderingProviderFullName',
        'RENDERING_PROVIDER_FIRST_NAME', 'RENDERING_PROVIDER_LAST_NAME', 'Status', 'STATUS', 'DEGREE', 'PCP', 'CAPITATED',
        'BOARD_CERTIFIED', 'ACCEPTING_NEW_PATIENTS', 'LANGUAGE', 'LANGUAGES', 'BoardCertified', 'AcceptingNewPatients', 'Language', 'Languages',
        'GENDER', 'Gender', 'gender', 'SEX', 'Sex', 'sex', 'PROVIDER_GENDER', 'RENDERING_PROVIDER_GENDER', 'RenderingProviderGender',
        'PRIMARY_SPECIALTY', 'SECONDARY_SPECIALTY', 'ORGANIZATION_NAME', 'ORG_NAME', 'SERVICE_LOCATION_ADDRESS_1', 'SERVICE_LOCATION_ADDRESS_2',
        'SERVICE_LOCATION_CITY', 'SERVICE_LOCATION_STATE', 'SERVICE_LOCATION_ZIP', 'SERVICE_LOCATION_PHONE', 'SERVICE_LOCATION_FAX',
        'SERVICE_LOCATION_LAT', 'SERVICE_LOCATION_LON', 'COUNTY', 'PHC', 'DPO', 'Locations', 'PCP_SERVICES', 'PHC_SERVICES', 'DPO_SERVICES',
        'PHC_PCP_COPAY', 'PHC_SPECIALIST_COPAY', 'PHC_NON_PCP_FIRST_VISIT', 'PHC_NON_PCP_FOLLOW_UP', 'CAPITATION_PAY_SUBSCRIBER_OR_FIRST', 'CAPITATION_PAY_DEPENDENTS_2_TO_5',
        'DPO_FIRST_VISIT', 'DPO_FOLLOW_UP', 'Copay', 'COPAY', 'MedicareFeeSchedule', 'MEDICARE_FEE_SCHEDULE', 'PHC_MEDICARE_ALLOWABLE_RATE', 'DPO_MEDICARE_ALLOWABLE_RATE',
        'PHC_MEDICAL_OR_OTHER_SERVICES', 'PHC_DIAGNOSTICS_SERVICES', 'DPO_MEDICAL_OR_OTHER_SERVICES', 'DPO_DIAGNOSTICS_SERVICES', 'COPAY_PRIMARY_CARE_CAPITATED',
        'source', 'isFacility', 'BILLING_PROVIDER_NAME', 'GROUP_PRACTICE', 'BILLING_PHONE', 'EMAIL', 'IPA_AFFILIATION',
        'ServiceLocationAddress1', 'ServiceLocationAddress2', 'ServiceLocationCity', 'ServiceLocationState', 'ServiceLocationZip',
        'ServiceLocationPhone', 'ServiceLocationFax', 'Address', 'ADDRESS', 'Phone', 'Fax'
      ]);
      const items = [];
      for (const k of Object.keys(p)) {
        if (known.has(k)) continue;
        const v = p[k];
        if (v == null || v === '' || (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0)) continue;
        const label = k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
        items.push({ label, value: Array.isArray(v) ? JSON.stringify(v) : String(v) });
      }
      return items.slice(0, 10);
    }
  },
  mounted() {
    if (this.contentOnly && this.providerNpi) {
      this.fetchProviderDetails();
    }
  },
  watch: {
    dialog(val) {
      if (val) {
        this.providerDetails = null;
        this.error = null;
        if (this.providerNpi) {
          this.fetchProviderDetails();
        } else {
          this.error = 'Provider NPI not found';
        }
      }
    },
    provider: {
      handler() {
        if (this.contentOnly || this.dialog) {
          this.providerDetails = null;
          this.error = null;
          if (this.providerNpi) {
            this.fetchProviderDetails();
          } else {
            this.error = 'Provider NPI not found';
          }
        }
      },
      deep: true
    }
  },
  methods: {
    openDialog() {
      this.dialog = true;
    },
    _isTruthy(v) {
      if (v == null) return false;
      if (typeof v === 'boolean') return v;
      const s = String(v).trim().toLowerCase();
      return s === 'yes' || s === 'y' || s === 'true' || s === '1' || s === 'on';
    },
    _get(...keys) {
      const p = this.providerDetails || this.provider;
      if (!p) return '';
      for (const k of keys) {
        if (p[k] != null && p[k] !== '') return p[k];
      }
      return '';
    },
    _getFromObj(obj, ...keys) {
      if (!obj) return '';
      for (const k of keys) {
        if (obj[k] != null && obj[k] !== '') return obj[k];
      }
      return '';
    },
    _normalizeServicesArray(arr) {
      if (!Array.isArray(arr)) return [];
      return arr
        .map((svc) => {
          if (!svc || typeof svc !== 'object') return null;
          const name = svc.name != null ? svc.name : (svc.Name != null ? svc.Name : (svc.NAME != null ? svc.NAME : ''));
          const cptCode = svc.cptCode != null ? svc.cptCode : (svc.CPT_CODE != null ? svc.CPT_CODE : (svc.CPTCode != null ? svc.CPTCode : ''));
          const feeSchedule = svc.feeSchedule != null ? svc.feeSchedule : (svc.FeeSchedule != null ? svc.FeeSchedule : (svc.FEE_SCHEDULE != null ? svc.FEE_SCHEDULE : ''));
          const feeScheduleType = svc.feeScheduleType != null ? svc.feeScheduleType : (svc.FeeScheduleType != null ? svc.FeeScheduleType : '');
          if (!name && !cptCode && !feeSchedule) return null;
          return { name, cptCode, feeSchedule, feeScheduleType };
        })
        .filter(Boolean);
    },
    async fetchProviderDetails() {
      const list = this.provider || {};
      const mongoId =
        list.MONGO_DOCUMENT_ID != null && String(list.MONGO_DOCUMENT_ID).trim() !== ''
          ? String(list.MONGO_DOCUMENT_ID).trim()
          : '';

      this.loading = true;
      this.error = null;
      try {
        if (mongoId) {
          try {
            const res = await axios.get('/api/providers/directory-row', { params: { id: mongoId } });
            if (res.data && res.data.error) {
              this.error = res.data.error;
            } else if (res.data) {
              this.providerDetails = res.data;
              return;
            }
          } catch (dirErr) {
            /* fallback: API por NPI pode devolver outra linha do mesmo NPI */
          }
        }

        const npi = list.PROVIDER_ID || list.provider_id || list.NPI;
        if (!npi) {
          this.error = 'Provider NPI not found';
          return;
        }
        const res = await axios.get(`/api/member-payments/providers/${npi}`);
        if (res.data && res.data.error) {
          this.error = res.data.error;
          return;
        }
        this.providerDetails = res.data;
      } catch (err) {
        this.error = (err.response && err.response.data && err.response.data.error) || err.message || 'Failed to load provider details';
      } finally {
        this.loading = false;
      }
    },
    formatPrice(value) {
      if (value == null || value === '') return '—';
      const n = parseFloat(String(value).replace(/[^0-9.-]/g, ''));
      if (isNaN(n)) return String(value);
      return '$' + (Number.isInteger(n) ? n : n.toFixed(2));
    },
    formatAmount(value, type, assumePercentIfMissing = false) {
      if (value == null || value === '') return '—';
      const n = parseFloat(String(value).replace(/[^0-9.-]/g, ''));
      if (isNaN(n)) return String(value);
      const typeStr = type != null ? String(type).toLowerCase().trim() : '';
      // percent: type "percent" (case-insensitive), decimal 0–1, ou assumePercent com inteiro 1–150
      const isPercent =
        typeStr === 'percent' ||
        (typeStr !== 'fixed' && typeStr !== 'dollar' && typeStr !== '$' && (
          (n > 0 && n <= 1) ||
          (assumePercentIfMissing && !typeStr && n >= 1 && n <= 150)
        ));
      if (isPercent) {
        const pct = n <= 1 && n > 0 ? Math.round(n * 100) : Math.round(n);
        return pct + '%';
      }
      return '$' + (Number.isInteger(n) ? n : n.toFixed(2));
    }
  }
};
</script>

<style scoped>
.provider-details >>> .v-list-item {
  min-height: 36px;
}

/* Page mode: full-page layout */
.provider-details-page-mode {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.provider-page-header {
  background: linear-gradient(135deg, rgba(34, 40, 49, 0.04) 0%, rgba(0, 173, 181, 0.06) 100%);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.provider-details-page-mode .provider-details .text-subtitle-2 {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(0, 0, 0, 0.5);
  margin-bottom: 8px;
}

.provider-details-page-mode .provider-details > div {
  margin-top: 24px;
}

.provider-details-page-mode .provider-details > div:first-child {
  margin-top: 0;
}

.provider-details-page-mode .provider-details .v-divider {
  margin: 20px 0 16px 0;
}

.provider-details-page-mode .v-simple-table {
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.provider-details-page-mode .v-simple-table thead th {
  background: rgba(0, 0, 0, 0.03) !important;
  font-weight: 600;
  font-size: 0.8125rem;
  padding: 12px 16px;
}

.provider-details-page-mode .v-simple-table tbody td {
  padding: 12px 16px;
}

.provider-details-page-mode .provider-details a {
  color: #222831;
  text-decoration: none;
  font-weight: 500;
}

.provider-details-page-mode .provider-details a:hover {
  text-decoration: underline;
}
</style>
